import { useState, useCallback, useRef } from 'react'

interface UseInViewOptions {
  threshold?: number
  rootMargin?: string
  triggerOnce?: boolean
}

export function useInView<T extends Element>(
  options: UseInViewOptions = {}
): [(node: T | null) => void, boolean] {
  const { threshold = 0.1, rootMargin = '0px', triggerOnce = true } = options
  const [inView, setInView] = useState(false)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const ref = useCallback(
    (node: T | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
      }

      if (!node) return

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setInView(true)
            if (triggerOnce) observer.disconnect()
          } else if (!triggerOnce) {
            setInView(false)
          }
        },
        { threshold, rootMargin }
      )

      observer.observe(node)
      observerRef.current = observer
    },
    [threshold, rootMargin, triggerOnce]
  )

  return [ref, inView]
}
